import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Label,
} from "reactstrap";
import Breadcrumbs from "../../coreComponents/breadcrumbs";
import { format, startOfMonth } from "date-fns";
import ApiService from "../../services/apiService";
import { toast } from "react-toastify";
import LeftIcon from "../svg/LeftButton";
import RightIcon from "../svg/RightIcon";
import ExportExcel from "../reports/ExportExcel";
import DateTimePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const PumpRunReport = () => {
  const [fromDateTime, setFromDateTime] = useState<any>(() =>
    startOfMonth(new Date()),
  );
  const [toDateTime, setToDateTime] = useState<any>(() => new Date());

  const [pumpingStationData, setPumpingStationData] = useState<any[]>([]);
  const [selectedStationName, setSelectedStationName] = useState<any>(null);
  const [selectedStationId, setSelectedStationId] = useState<any>(null);
  const [stationDropdownOpen, setStationDropdownOpen] = useState(false);

  const [data, setData] = useState<any[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const pageSize = 10;
  const [totalCount, setTotalCount] = useState<number>(0);
  const totalPages = Math.ceil(totalCount / pageSize);

  const pumps = [
     { label: "PMP1", dataKey: "pmp1", runMinKey: "pmP1_RunMin" },
     { label: "PMP2", dataKey: "pmp2", runMinKey: "pmP2_RunMin" },
     { label: "PMP3", dataKey: "pmp3", runMinKey: "pmP3_RunMin" },
     { label: "PMP4", dataKey: "pmp4", runMinKey: "pmP4_RunMin" },
     { label: "PMP5", dataKey: "pmp5", runMinKey: "pmP5_RunMin" },
   ];

  useEffect(() => {
    const getPumpingStation = async () => {
      try {
        const response = await ApiService.axiosApi(`/pumpingstation`, {
          method: "GET",
          params: { pageNumber: 0, pageSize: 0 },
        });
        if (response.success && response.data.data.length > 0) {
          setPumpingStationData(response.data.data);
          setSelectedStationName(response.data.data[0].name);
          setSelectedStationId(response.data.data[0].id);
        }
      } catch (e) {
        console.log(e);
      }
    };
    getPumpingStation();
  }, []);

  const validateGenerate = () => {
    if (!selectedStationName) {
      toast.warning("Please select Pumping Station");
      return false;
    }
    if (!fromDateTime) {
      toast.warning("Please select From Date");
      return false;
    }
    if (!toDateTime) {
      toast.warning("Please select To Date");
      return false;
    }
    if (new Date(fromDateTime) > new Date(toDateTime)) {
      toast.warning("From Date cannot be greater than To Date");
      return false;
    }
    return true;
  };

  const fetchData = async (pNum = pageNumber) => {
     setData([]); 
    try {
      const response = await ApiService.axiosApi(
        `/PumpingStation/getallpumprunhrsandonoff`, // ← update your endpoint
        {
          method: "GET",
          params: {
            pageNumber: pNum,
            pageSize,
            fromDate: format(fromDateTime, "yyyy-MM-dd HH:mm:ss"),
            toDate: format(toDateTime, "yyyy-MM-dd HH:mm:ss"),
            Id: selectedStationId,
          },
        },
      );
      if (response.success) {
        setTotalCount(response.data.totalCount ?? 0);
        setData(response.data.data ?? []);
        if ((response.data.data ?? []).length === 0)
          toast.error("No data found");
      } else {
        toast.error(response.data);
      }
    } catch (e) {
      console.log(e);
      toast.error("Unable to get data at this moment");
    }
  };

  const getAllDataForExport = async () => {
     try {
       const response = await ApiService.axiosApi(
         `/PumpingStation/getallpumprunhrsandonoff`,
         {
           method: "GET",
           params: {
             pageNumber: 0,
             pageSize: 0,
             fromDate: format(fromDateTime, "yyyy-MM-dd HH:mm:ss"),
             toDate: format(toDateTime, "yyyy-MM-dd HH:mm:ss"),
             Id: selectedStationId,
           },
         },
       );
       if (response.success) {
         const allData = response.data.data ?? [];
   
         // Map data rows to match table structure
         const exportRows = allData.map((row: any) => ({
           "Date":        format(new Date(row.dateTime), "dd-MM-yyyy HH:mm"),
           "PMP1":        row.pmp1 ?? "-",
           "PMP2":        row.pmp2 ?? "-",
           "PMP3":        row.pmp3 ?? "-",
           "PMP4":        row.pmp4 ?? "-",
           "PMP5":        row.pmp5 ?? "-",
           "PMP1 RunHrs": row.pmP1_RunMin ?? "00:00",
           "PMP2 RunHrs": row.pmP2_RunMin ?? "00:00",
           "PMP3 RunHrs": row.pmP3_RunMin ?? "00:00",
           "PMP4 RunHrs": row.pmP4_RunMin ?? "00:00",
           "PMP5 RunHrs": row.pmP5_RunMin ?? "00:00",
         }));
   
         // Footer row — total HH:mm for each pump
         const totalRow: any = {
           "Date": "Total Run (HH:mm)",
           "PMP1": "", "PMP2": "", "PMP3": "", "PMP4": "", "PMP5": "",
         };
         pumps.forEach((p) => {
           const totalMins = allData.reduce(
             (sum: number, row: any) => sum + timeToMinutes(row[p.runMinKey]),
             0
           );
           const h = Math.floor(totalMins / 60);
           const m = totalMins % 60;
           const label = `PMP${p.label.replace("PMP", "")} RunHrs`;
           totalRow[label] = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
         });
   
         exportRows.push(totalRow);
         return exportRows;
       }
     } catch (e) {
       console.log(e);
     }
   };
  useEffect(() => {
     if (selectedStationId) {
       fetchData(1);
     }
   }, [selectedStationId]);
  useEffect(() => {
    if (selectedStationId) fetchData(pageNumber);
  }, [pageNumber]);

  const handleNext = useCallback(() => {
    setPageNumber((prev) => Math.min(totalPages, prev + 1));
  }, [totalPages]);

  const handlePrev = useCallback(() => {
    setPageNumber((prev) => Math.max(1, prev - 1));
  }, []);
  const timeToMinutes = (val: string): number => {
     if (!val || val === "-") return 0;
     const [h, m] = val.split(":").map(Number);
     return (h || 0) * 60 + (m || 0);
   };
  // Footer: sum of each RunMin column across current page
  const footer = pumps.reduce((acc, p) => {
     const totalMins = data.reduce(
       (sum, row) => sum + timeToMinutes(row[p.runMinKey]),
       0
     );
     const h = Math.floor(totalMins / 60);
     const m = totalMins % 60;
     acc[p.runMinKey] = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
     return acc;
   }, {} as Record<string, string>);
  return (
    <>
      <Breadcrumbs
        mainTitle="RunHours & OnOff"
        parent="Pumping Station Reports"
      />
      <div className="w-100 bg-white px-2 py-2">
        {/* Filter Bar */}
        <div
          className="bg-light-primary d-flex justify-content-around align-items-center flex-wrap"
          style={{
            width: "100%",
            minHeight: "100px",
            border: "1px solid rgb(43,94,94,0.1)",
            padding: "8px 0",
          }}
        >
          {/* Pumping Station */}
          <div className="report-input-div mx-4 input-border d-flex flex-column">
            <Label>Pumping Station:</Label>
            <Dropdown
              isOpen={stationDropdownOpen}
              toggle={() => setStationDropdownOpen(!stationDropdownOpen)}
              className="custom-dropdown-wrapper"
            >
              <DropdownToggle
                className="custom-dropdown-toggle d-flex align-items-center justify-content-between report-dropdown"
                style={{
                  fontWeight: 500,
                  borderRadius: "5px",
                  padding: "8px 16px",
                  width: "200px",
                  height: "36px",
                }}
                color="whitesmoke"
              >
                {selectedStationName ?? "Pumping Station"}
              </DropdownToggle>
              <DropdownMenu
                className="custom-dropdown-menu"
                style={{
                  overflowY: "auto",
                  boxShadow: "0 0.5rem 1rem rgba(0,0,0,0.1)",
                  borderRadius: "8px",
                  padding: "8px 0",
                }}
              >
                {pumpingStationData.map((station: any) => (
                  <DropdownItem
                    key={station.id}
                    onClick={() => {
                      setSelectedStationName(station.name);
                      setSelectedStationId(station.id);
                    }}
                  >
                    {station.name}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>

          {/* From DateTime */}
          <div className="report-input-div mx-4 input-border d-flex flex-column">
            <Label>From Date:</Label>
            <DateTimePicker
              className="report-input"
                 dateFormat="dd/MM/yyyy HH:mm"
               timeFormat="HH:mm"
              placeholderText="DD-MM-YYYY"
              onChange={(date: any) => setFromDateTime(date)}
              selected={fromDateTime}
              isClearable
              showTimeSelect
            />
          </div>

          {/* To DateTime */}
          <div className="report-input-div mx-4 input-border d-flex flex-column">
            <Label>To Date:</Label>
            <DateTimePicker
              className="report-input"
                  dateFormat="dd/MM/yyyy HH:mm"
               timeFormat="HH:mm"
              placeholderText="DD-MM-YYYY"
              onChange={(date: any) => setToDateTime(date)}
              selected={toDateTime}
              isClearable
              showTimeSelect
            />
          </div>

          {/* Generate */}
          <div className="d-flex flex-column" style={{ width: "8%" }}>
            <Label style={{ visibility: "hidden" }}>Generate</Label>
            <Button
              className="px-0"
              color="success"
              onClick={() => {
                if (!validateGenerate()) return;
                setPageNumber(1);
                fetchData(1);
              }}
              style={{
                width: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Generate
            </Button>
          </div>

          {/* Export */}
          <div style={{ width: "8%" }}>
            <Label style={{ visibility: "hidden" }}>Export</Label>
            <ExportExcel
              getData={getAllDataForExport}
              fileName="RunHours & OnOff"
              EffectiveFrom={
                fromDateTime ? format(fromDateTime, "yyyy-MM-dd HH:mm:ss") : ""
              }
              toDate={
                toDateTime ? format(toDateTime, "yyyy-MM-dd HH:mm:ss") : ""
              }
              hiddenColumns={[]}
              columnOrder={[
               "Date",
               "PMP1", "PMP2", "PMP3", "PMP4", "PMP5",
               "PMP1 RunHrs", "PMP2 RunHrs", "PMP3 RunHrs", "PMP4 RunHrs", "PMP5 RunHrs",
             ]}
            />
          </div>
        </div>

        {/* Table */}
        <div
          className="mt-2"
          style={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}
        >
          <table
            className="table table-sm table-hover text-center"
            style={{
              fontSize: "0.95rem",
              fontFamily: "Segoe UI, Roboto, sans-serif",
              width: "100%",
            }}
          >
            <thead className="bg-light-primary">
              <tr>
                <th
                  style={{
                    fontWeight: 600,
                    color: "#333",
                    padding: "10px 8px",
                    fontSize: "1rem",
                    verticalAlign: "middle",
                    whiteSpace: "nowrap",
                  }}
                >
                  Date
                </th>
                {pumps.map((p) => (
                  <th
                    key={p.label}
                    style={{
                      fontWeight: 600,
                      color: "#333",
                      padding: "10px 8px",
                      fontSize: "1rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {p.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="table-body">
              {data.length > 0 ? (
                data.map((row, idx) => (
                  <tr key={idx}>
                    <td
                      style={{
                        padding: "6px 8px",
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {format(new Date(row.dateTime), "dd-MM-yyyy HH:mm")}
                    </td>
                    {pumps.map((p) => (
                      <td key={p.label} style={{ padding: "6px 8px" }}>
                        {row[p.dataKey] ?? "-"}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={pumps.length + 1}
                    className="text-center text-muted py-3"
                  >
                    No data available
                  </td>
                </tr>
              )}
            </tbody>

            {/* Footer — RunMin totals */}
            {/* Footer — RunMin totals */}
            {data.length > 0 && (
              <tfoot>
               <tr className="bg-light-primary">
               <td style={{ padding: "8px", textAlign: "center", whiteSpace: "nowrap", fontWeight: 600, color: "#333" }}>
                  Total Run (HH:mm)
                </td>
                {pumps.map((p) => (
                   <td key={p.runMinKey} style={{ padding: "8px", fontWeight: 600, color: "#333" }}>
                    {footer[p.runMinKey]}
                  </td>
                ))}
              </tr>
            </tfoot>
            )}
          </table>
        </div>

        {/* Pagination */}
        <div
          className="d-flex justify-content-end align-content-center"
          style={{ marginRight: "10px", marginTop: "10px" }}
        >
          <p style={{ marginRight: "10px", marginTop: "8px" }}>
            Page {pageNumber} of {totalPages || 1}
          </p>
          <button
            onClick={handlePrev}
            disabled={pageNumber === 1}
            className={`icon-button ${pageNumber === 1 ? "disabled" : ""}`}
          >
            <LeftIcon className="icon" />
          </button>
          <button
            onClick={handleNext}
            disabled={pageNumber === totalPages || totalPages === 0}
            className={`icon-button ${pageNumber === totalPages ? "disabled" : ""}`}
          >
            <RightIcon className="icon" />
          </button>
        </div>
      </div>
    </>
  );
};

export default PumpRunReport;
